/**
 * SMS segment calculation (GSM-7bit / UCS-2), ported from backend example + OpenAPI limits.
 *
 * GSM-7bit: 160 chars single, 153 per concatenated segment
 * UCS-2:    70 chars single,  67 per concatenated segment
 * Max:      10 segments
 */

export const MAX_SEGMENTS = 10;
export const SINGLE_GSM_MAX = 160;
export const CONCAT_GSM_MAX = 153;
export const SINGLE_UCS2_MAX = 70;
export const CONCAT_UCS2_MAX = 67;
export const PRICE_PER_SEGMENT = 0.1;

/** Basic GSM 03.38 character set */
const GSM_7BIT_CHARS =
    '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?' +
    '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';

/** GSM 03.38 extended characters (escape-prefixed; presence still counts as GSM) */
const GSM_7BIT_EXTENDED = '^{}\\[~]|€';

const GSM_7BIT_ALL = GSM_7BIT_CHARS + GSM_7BIT_EXTENDED;

/**
 * @param {string} text
 * @returns {boolean}
 */
function isGsm7bit(text) {
    if (text.includes('[firstname]') || text.includes('[lastname]')) {
        return false;
    }

    const chars = [...text];
    for (let i = 0; i < chars.length; i += 1) {
        const char = chars[i];
        if (char !== '\\' && !GSM_7BIT_ALL.includes(char)) {
            return false;
        }
    }

    return true;
}

/**
 * Length after template-variable adjustments (same rules as backend).
 * @param {string} text
 * @returns {number}
 */
function getAdjustedLength(text) {
    const chars = [...text];
    let { length } = chars;

    if (text.includes('[lastname]')) length += 6;
    if (text.includes('[date]')) length += 4;
    if (text.includes('[playernumber]')) length -= 8;
    if (text.includes('[pay_link]')) length += 90;
    if (text.includes('[site_link]')) length += 80;

    return length;
}

/**
 * @param {string} text
 * @param {number} [maxSegments=MAX_SEGMENTS]
 * @returns {{ segments: number|null, encoding: 'gsm'|'ucs2'|null, adjustedLength: number, isValid: boolean }}
 */
export function getSmsSegmentInfo(text, maxSegments = MAX_SEGMENTS) {
    const raw = text ?? '';
    const adjustedLength = getAdjustedLength(raw);

    if (adjustedLength <= 0 || raw.length === 0) {
        return {
            segments: null,
            encoding: null,
            adjustedLength: 0,
            isValid: false,
        };
    }

    const gsm = isGsm7bit(raw);
    const encoding = gsm ? 'gsm' : 'ucs2';
    const singleMax = gsm ? SINGLE_GSM_MAX : SINGLE_UCS2_MAX;
    const concatMax = gsm ? CONCAT_GSM_MAX : CONCAT_UCS2_MAX;

    const segments = adjustedLength <= singleMax ? 1 : Math.ceil(adjustedLength / concatMax);

    if (segments > maxSegments) {
        return {
            segments,
            encoding,
            adjustedLength,
            isValid: false,
        };
    }

    return {
        segments,
        encoding,
        adjustedLength,
        isValid: true,
    };
}

/**
 * Estimated total price in PLN.
 * @param {number} recipients
 * @param {number|null} segments
 * @returns {number}
 */
export function estimateSmsPrice(recipients, segments) {
    if (!recipients || !segments) return 0;
    return Math.round(recipients * segments * PRICE_PER_SEGMENT * 100) / 100;
}
